import { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { CheckCircle, Error as ErrorIcon, Warning } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { testIds } from "./OrgDataTransfer.testIds";

export interface Organization {
  _id: string;
  _organization: string;
  name: string;
  dotNumber: string | null | undefined;
}

export interface CollectionTransferResult {
  collectionName: string;
  recordCount: number;
  success: boolean;
  error?: string | null;
}

export interface TransferResult {
  success: boolean;
  message: string;
  transferredCollections: CollectionTransferResult[];
  totalRecordsTransferred: number;
  duration: number;
  timestamp: string;
}

export interface OrgDataTransferProps {
  onFetchOrganizationsForStage: (stage: string) => Promise<Organization[]>;
  onTransfer: (
    sourceOrgId: string,
    sourceStage: string,
    targetOrgId: string,
    targetStage: string,
    deleteExistingData?: boolean
  ) => Promise<TransferResult>;
  isTransferring: boolean;
}

export default function OrgDataTransfer({
  onFetchOrganizationsForStage,
  onTransfer,
  isTransferring,
}: OrgDataTransferProps) {
  const [sourceStage, setSourceStage] = useState<string>("qa");
  const [targetStage, setTargetStage] = useState<string>("qa");
  const [sourceOrganizations, setSourceOrganizations] = useState<
    Organization[]
  >([]);
  const [targetOrganizations, setTargetOrganizations] = useState<
    Organization[]
  >([]);
  const [isLoadingSourceOrgs, setIsLoadingSourceOrgs] =
    useState<boolean>(false);
  const [isLoadingTargetOrgs, setIsLoadingTargetOrgs] =
    useState<boolean>(false);
  const [sourceOrgId, setSourceOrgId] = useState<string>("");
  const [targetOrgId, setTargetOrgId] = useState<string>("");
  const [deleteExistingData, setDeleteExistingData] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [transferResult, setTransferResult] =
    useState<TransferResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stages = [
    { value: "production", label: "Production" },
    { value: "beta", label: "Beta" },
    { value: "qa", label: "QA" },
  ];

  // Target stages exclude production to prevent accidental data transfers to production
  const targetStages = [
    { value: "beta", label: "Beta" },
    { value: "qa", label: "QA" },
  ];

  // Fetch source organizations when source stage changes
  const fetchSourceOrganizations = async (stage: string) => {
    setIsLoadingSourceOrgs(true);
    setSourceOrgId("");
    try {
      const orgs = await onFetchOrganizationsForStage(stage);
      setSourceOrganizations(orgs);
    } catch (err: any) {
      setError(`Failed to fetch organizations for ${stage}: ${err.message}`);
      setSourceOrganizations([]);
    } finally {
      setIsLoadingSourceOrgs(false);
    }
  };

  // Fetch target organizations when target stage changes
  const fetchTargetOrganizations = async (stage: string) => {
    setIsLoadingTargetOrgs(true);
    setTargetOrgId("");
    try {
      const orgs = await onFetchOrganizationsForStage(stage);
      setTargetOrganizations(orgs);
    } catch (err: any) {
      setError(`Failed to fetch organizations for ${stage}: ${err.message}`);
      setTargetOrganizations([]);
    } finally {
      setIsLoadingTargetOrgs(false);
    }
  };

  // Load initial organizations
  useEffect(() => {
    fetchSourceOrganizations(sourceStage);
    fetchTargetOrganizations(targetStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSourceStageChange = (stage: string) => {
    setSourceStage(stage);
    fetchSourceOrganizations(stage);
  };

  const handleTargetStageChange = (stage: string) => {
    setTargetStage(stage);
    fetchTargetOrganizations(stage);
  };

  const handleTransferClick = () => {
    if (!sourceOrgId || !targetOrgId) {
      setError("Please select both source and target organizations");
      return;
    }

    if (sourceOrgId === targetOrgId && sourceStage === targetStage) {
      setError(
        "Source and target must be different (different organization or different stage)"
      );
      return;
    }

    setError(null);

    // Show confirmation dialog if delete option is checked
    if (deleteExistingData) {
      setShowConfirmDialog(true);
    } else {
      handleTransfer();
    }
  };

  const handleTransfer = async () => {
    setShowConfirmDialog(false);
    setTransferResult(null);

    try {
      const result = await onTransfer(
        sourceOrgId,
        sourceStage,
        targetOrgId,
        targetStage,
        deleteExistingData
      );
      setTransferResult(result);
    } catch (err: any) {
      setError(err.message || "Failed to transfer organization data");
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  const canTransfer = sourceOrgId && targetOrgId && !isTransferring;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Organization Data Transfer
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Transfer data from one organization to another for demo and testing
          purposes.
        </Typography>

        <Stack spacing={3} sx={{ mt: 3 }}>
          {/* Source Stage Selection */}
          <FormControl fullWidth>
            <InputLabel id="source-stage-label">Source Stage</InputLabel>
            <Select data-testid={testIds.orgDataTransferSourceStage}
              labelId="source-stage-label"
              id="source-stage-select"
              value={sourceStage}
              label="Source Stage"
              onChange={(e) => handleSourceStageChange(e.target.value)}
              disabled={isTransferring}
            >
              {stages.map((stage) => (
                <MenuItem data-testid={`${testIds.orgDataTransferSourceStageOption}-${stage.value}`} key={stage.value} value={stage.value}>
                  {stage.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Source Organization Selection */}
          <Autocomplete<Organization> data-testid={testIds.orgDataTransferSourceOrganizationPicker}
            id="source-org-select"
            options={sourceOrganizations}
            getOptionLabel={(option) =>
              `${option.name}${option.dotNumber ? ` (${option.dotNumber})` : ""}`
            }
            value={
              sourceOrganizations.find(
                (org) => org._organization === sourceOrgId
              ) || null
            }
            onChange={(_, newValue) => {
              setSourceOrgId(newValue?._organization || "");
            }}
            disabled={isTransferring}
            loading={isLoadingSourceOrgs}
            isOptionEqualToValue={(option, value) =>
              option._organization === value._organization
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Source Organization"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingSourceOrgs ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Target Stage Selection */}
          <FormControl fullWidth>
            <InputLabel id="target-stage-label">Target Stage</InputLabel>
            <Select data-testid={testIds.orgDataTransferTargetStage}
              labelId="target-stage-label"
              id="target-stage-select"
              value={targetStage}
              label="Target Stage"
              onChange={(e) => handleTargetStageChange(e.target.value)}
              disabled={isTransferring}
            >
              {targetStages.map((stage) => (
                <MenuItem data-testid={`${testIds.orgDataTransferTargetStageOption}-${stage.value}`} key={stage.value} value={stage.value}>
                  {stage.label}
                </MenuItem>
              ))}
            </Select>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, ml: 1.5 }}
            >
              Production is excluded to prevent accidental data transfers
            </Typography>
          </FormControl>

          {/* Target Organization Selection */}
          <Autocomplete<Organization> data-testid={testIds.orgDataTransferTargetOrganizationPicker}
            id="target-org-select"
            options={targetOrganizations}
            getOptionLabel={(option) =>
              `${option.name}${option.dotNumber ? ` (${option.dotNumber})` : ""}`
            }
            value={
              targetOrganizations.find(
                (org) => org._organization === targetOrgId
              ) || null
            }
            onChange={(_, newValue) => {
              setTargetOrgId(newValue?._organization || "");
            }}
            disabled={isTransferring}
            loading={isLoadingTargetOrgs}
            isOptionEqualToValue={(option, value) =>
              option._organization === value._organization
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Organization"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingTargetOrgs ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Delete Existing Data Checkbox */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteExistingData}
                  inputProps={{ "data-testid": testIds.orgDataTransferDeleteExistingData } as React.InputHTMLAttributes<HTMLInputElement>}
                  onChange={(e) => setDeleteExistingData(e.target.checked)}
                  disabled={isTransferring}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Warning color="warning" fontSize="small" />
                  <Typography variant="body2">
                    Delete existing data in target organization before transfer
                  </Typography>
                </Box>
              }
            />
            {deleteExistingData && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Warning: This will permanently delete all existing data in the
                target organization before transferring new data.
              </Alert>
            )}
          </Box>

          {/* Transfer Button */}
          <LoadingButton data-testid={testIds.orgDataTransferStartTransfer}
            variant="contained"
            color="primary"
            onClick={handleTransferClick}
            disabled={!canTransfer}
            loading={isTransferring}
            loadingIndicator={
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <span>Transferring...</span>
              </Stack>
            }
          >
            Transfer Data
          </LoadingButton>

          {/* Confirmation Dialog */}
          <Dialog
            open={showConfirmDialog}
            onClose={handleCancelConfirm}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <DialogTitle id="confirm-dialog-title">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Warning color="error" />
                <span>Confirm Data Deletion</span>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="confirm-dialog-description">
                You are about to permanently delete all existing data in the
                target organization before transferring new data. This action
                cannot be undone.
                <br />
                <br />
                <strong>Are you sure you want to continue?</strong>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button data-testid={testIds.orgDataTransferCancel} onClick={handleCancelConfirm} color="primary">
                Cancel
              </Button>
              <Button data-testid={testIds.orgDataTransferConfirmTransfer}
                onClick={handleTransfer}
                color="error"
                variant="contained"
                autoFocus
              >
                Yes, Delete and Transfer
              </Button>
            </DialogActions>
          </Dialog>

          {/* Error Display */}
          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {/* Transfer Result Display */}
          {transferResult && (
            <Box>
              <Alert
                severity={transferResult.success ? "success" : "error"}
                icon={transferResult.success ? <CheckCircle /> : <ErrorIcon />}
              >
                {transferResult.message}
              </Alert>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Transfer Summary
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Total Records Transferred:</strong>{" "}
                    {transferResult.totalRecordsTransferred.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong>{" "}
                    {(transferResult.duration / 1000).toFixed(2)} seconds
                  </Typography>
                  <Typography variant="body2">
                    <strong>Timestamp:</strong>{" "}
                    {new Date(transferResult.timestamp).toLocaleString()}
                  </Typography>
                </Stack>
              </Box>

              {/* Collections Table */}
              {transferResult.transferredCollections.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Collections Transferred
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Collection</TableCell>
                          <TableCell align="right">Records</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transferResult.transferredCollections.map(
                          (collection) => (
                            <TableRow key={collection.collectionName}>
                              <TableCell>{collection.collectionName}</TableCell>
                              <TableCell align="right">
                                {collection.recordCount.toLocaleString()}
                              </TableCell>
                              <TableCell align="center">
                                {collection.success ? (
                                  <CheckCircle
                                    color="success"
                                    fontSize="small"
                                  />
                                ) : (
                                  <ErrorIcon color="error" fontSize="small" />
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
